import { useMemo } from "react"

import { useSessionsQuery } from "@/features/chat/hooks/use-sessions-query"
import { useCurrentSessionType } from "@/hooks/use-current-session-type"
import { useSessionId } from "@/hooks/use-session-id"
import { ChatService } from "@/services/chat-service"

/**
 * Hook to get the project ID of the currently active chat session.
 * Falls back to the in-memory ChatService session to handle the case where
 * the session list query hasn't caught up yet (e.g. directly after opening a session).
 * Returns the project ID if the current session is linked to a project, null otherwise.
 */
export function useCurrentSessionProject() {
  const currentSessionId = useSessionId()
  const sessionType = useCurrentSessionType()

  const { data: sessions = [] } = useSessionsQuery(sessionType)

  const currentSessionProjectId = useMemo(() => {
    if (!currentSessionId) return null

    // Primary: look in the query cache
    const querySession = sessions.find((session) => session?.id === currentSessionId)
    if (querySession?.projectId) {
      return querySession.projectId
    }

    // Fallback: look in the in-memory open sessions to handle race with query invalidation
    const openSession = ChatService.getInstance().getSessionById(currentSessionId)
    if (openSession?.projectId) {
      return openSession.projectId
    }

    return null
  }, [currentSessionId, sessions])

  return currentSessionProjectId
}
